// API client for admin-api
// El cliente HTTP vive en `peticion.ts`; el conocimiento, en `api-conocimiento.ts`.

import { conocimiento } from './api-conocimiento.ts';
import { request } from './peticion.ts';

// Tenant endpoints
export type TipoDeCorreccion = 'conocimiento' | 'comportamiento' | 'defecto';

export const api = {
  /**
   * El alta pública. **Sin token**: la usa alguien que todavía no tiene cuenta.
   *
   * `POST /alta` existía desplegado desde el 2026-09-01 y ninguna pantalla lo
   * llamaba, así que no había forma de conseguir una cuenta.
   */
  alta: {
    registrarse: (body: { nombreDelNegocio: string; correo: string; contrasena: string }) =>
      request<{ slug: string }>('/alta', { method: 'POST', body: JSON.stringify(body) }),

    /**
     * Confirma el correo con el código y **deja el `UserRecord` escrito**.
     * Sin esa segunda parte, confirmar no alcanza: `requirePermission` devuelve
     * 401 mientras no exista el registro en DynamoDB.
     */
    confirmar: (correo: string, codigo: string) =>
      request<{ ok: boolean; tenantId: string }>('/alta/confirmar', {
        method: 'POST',
        body: JSON.stringify({ correo, codigo }),
      }),
  },

  tenants: {
    // Lo que el bot sabe y cómo se lo enseña, en su propio archivo (D4).
    ...conocimiento,

    /**
     * El dueño le enseña al bot (GKTIBO-115).
     *
     * **Propone, no guarda.** Devuelve el texto para que el dueño lo lea y lo
     * apruebe; quien escribe es `cerrarTema`, el mismo camino que usa la
     * entrevista.
     */
    enseñar: (id: string, body: { pregunta: string; respuesta: string; leccion: string }) =>
      request<{
        /** Qué es la corrección: decide a dónde va (ADR-031 D3). */
        tipo: TipoDeCorreccion;
        /** Con tema si es conocimiento; sin tema si es una regla. */
        propuestas: Array<{ tema?: string; texto: string }>;
        sinAsistente: boolean;
      }>(`/admin/tenants/${id}/probar/leccion`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    /**
     * Acá SÍ se guarda la corrección, como un hecho según su tipo
     * (GKTIBO-146). Reemplaza a `cerrarTema` desde «probá tu bot»: aquél
     * reemplazaba la sección entera, y así Catdog perdió lo que le enseñaron.
     */
    guardarCorreccion: (
      id: string,
      body: {
        tipo: TipoDeCorreccion;
        tema?: string;
        texto: string;
        pregunta?: string;
        respuesta?: string;
        turno?: string;
      },
    ) =>
      request<{ ok: boolean; destino: string }>(`/admin/tenants/${id}/conocimiento/correccion`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    /**
     * El dueño le escribe a su bot antes de que lo vea un cliente (GKTIBO-73).
     *
     * `origen` es la razón de ser de la pantalla: dice si la respuesta salió
     * del conocimiento, de herramientas, o **de ningún lado** — que es el caso
     * en que el bot está inventando.
     */
    /**
     * «Ya probé» — la marca explícita del botón de continuar (GKTIBO-123).
     *
     * Separado del camino de cada mensaje porque allá un fallo se traga para
     * que el dueño igual tenga su respuesta. Acá **no**: si no queda anotado,
     * el alta no avanza y hay que decírselo.
     */
    yaProbe: (id: string) =>
      request<{ ok: boolean }>(`/admin/tenants/${id}/probar/ya-probe`, { method: 'POST' }),

    probar: (id: string, body: { sesionId: string; mensaje: string }) =>
      request<{
        respuesta: string;
        origen:
          | { tipo: 'conocimiento'; secciones: string[] }
          | { tipo: 'herramientas'; cuantas: number }
          | { tipo: 'ninguno' };
        sesionNueva: boolean;
        restantes: number;
        simuladas: Array<{ herramienta: string; enVivo: string }>;
        /** De qué herramientas dispone el bot (GKTIBO-76). */
        herramientas?: string[];
      }>(`/admin/tenants/${id}/probar`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    list: () => request<{ tenants: import('./types.ts').Tenant[] }>('/admin/tenants'),
    get: (id: string) =>
      request<{
        tenant: import('./types.ts').Tenant;
        /** Config guardada (parcial si el tenant está en onboarding). */
        config?: Record<string, unknown> | null;
      }>(`/admin/tenants/${id}`),
    create: (body: {
      tenantId: string;
      name: string;
      botMode: 'informational' | 'transactional' | 'hybrid';
    }) =>
      request<{ tenantId: string; status: string }>('/admin/tenants', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    setStatus: (id: string, status: import('./types.ts').TenantStatus) =>
      request<{ tenantId: string; status: string }>(`/admin/tenants/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }),
    // Wizard step 2 — channel config (full schema, atomic with CHANNEL_LOOKUP)
    setChannels: (id: string, channels: import('./types.ts').FullChannelConfig[]) =>
      request<{ tenantId: string; channels: number }>(`/admin/tenants/${id}/channels`, {
        method: 'PATCH',
        body: JSON.stringify({ channels }),
      }),
    // Wizard step 3 — ERP config
    setErp: (id: string, erp: import('./types.ts').ErpConfig) =>
      request<{ tenantId: string; erp: string }>(`/admin/tenants/${id}/erp`, {
        method: 'PATCH',
        body: JSON.stringify(erp),
      }),
    // Wizard step 3 — flow config
    setFlow: (id: string, flow: import('./types.ts').FlowConfig) =>
      request<{ tenantId: string; flowType: string }>(`/admin/tenants/${id}/flow`, {
        method: 'PATCH',
        body: JSON.stringify(flow),
      }),
    setBotMode: (id: string, mode: 'informational' | 'transactional' | 'hybrid') =>
      request<{ tenantId: string; mode: string }>(`/admin/tenants/${id}/bot`, {
        method: 'PATCH',
        body: JSON.stringify({ mode }),
      }),
    // Wizard step 4 — copy, features
    setCopy: (id: string, copy: import('./types.ts').CopyConfig) =>
      request<import('./types.ts').CopyConfig>(`/admin/tenants/${id}/copy`, {
        method: 'PATCH',
        body: JSON.stringify(copy),
      }),
    uploadAiKey: (
      tenantId: string,
      body: { provider: 'anthropic' | 'openai' | 'gemini'; apiKey: string },
    ) =>
      request<{ arn: string }>(`/admin/tenants/${tenantId}/secrets/ai-key`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    patchAi: (tenantId: string, body: import('@chatbot/shared-types').AiConfig) =>
      request<void>(`/admin/tenants/${tenantId}/ai`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    uploadTelegramToken: (tenantId: string, token: string) =>
      // `bot` viene de verificar el token contra Telegram antes de guardarlo
      // (GKTIBO-62): es la confirmación de que conectó el que era.
      request<{
        arn: string;
        action: 'created' | 'updated';
        bot?: { username?: string; nombre?: string };
      }>(`/admin/tenants/${tenantId}/secrets/telegram-token`, {
        method: 'POST',
        body: JSON.stringify({ token }),
      }),
    setupTelegramWebhook: (tenantId: string) =>
      request<{ ok: boolean; webhookUrl: string }>(
        `/admin/tenants/${tenantId}/channels/telegram/setup-webhook`,
        { method: 'POST', body: JSON.stringify({}) },
      ),
    setupWhatsAppWebhook: (tenantId: string, body: { appId: string; phoneNumberId: string }) =>
      request<{
        ok: boolean;
        webhookUrl: string;
        verifyTokenSecretArn: string;
        instructions?: string;
      }>(`/admin/tenants/${tenantId}/channels/whatsapp/setup-webhook`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    uploadWhatsAppSecrets: (
      tenantId: string,
      body: { appSecret: string; accessToken: string; phoneNumberId: string },
    ) =>
      request<{ arns: string[]; phoneNumberId: string }>(
        `/admin/tenants/${tenantId}/secrets/whatsapp`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    getOnboardingStatus: (tenantId: string) =>
      request<import('./types.ts').OnboardingStatus>(
        `/admin/tenants/${tenantId}/onboarding-status`,
      ),
  },
  /**
   * La cola de tickets (EPIC-V2-TICKETS).
   *
   * El `tenantId` va en el PATH y no en el query string: `requirePermission`
   * compara contra el path param y no mira el query string, así que con la ruta
   * así el aislamiento lo garantiza el middleware.
   */
  tickets: {
    list: (tenantId: string, opts: { abiertos?: boolean; cursor?: string } = {}) => {
      const qs = new URLSearchParams({
        abiertos: String(opts.abiertos ?? true),
        ...(opts.cursor ? { cursor: opts.cursor } : {}),
      });
      return request<import('./types.ts').TicketsResponse>(
        `/admin/tickets/${tenantId}?${qs.toString()}`,
      );
    },
    assign: (
      tenantId: string,
      caso: { caseId: string; userId: string; openedAt: string },
      assignedTo: string,
    ) =>
      request<{ caseId: string; assignedTo: string }>(
        `/admin/tickets/${tenantId}/${caso.caseId}/assign`,
        {
          method: 'PATCH',
          body: JSON.stringify({ userId: caso.userId, openedAt: caso.openedAt, assignedTo }),
        },
      ),
    /** `status` es un estado NO terminal del vocabulario del tenant. */
    reopen: (
      tenantId: string,
      caso: { caseId: string; userId: string; openedAt: string },
      status: string,
    ) =>
      request<{ caseId: string; status: string }>(
        `/admin/tickets/${tenantId}/${caso.caseId}/reopen`,
        {
          method: 'POST',
          body: JSON.stringify({ userId: caso.userId, openedAt: caso.openedAt, status }),
        },
      ),
  },
  metrics: {
    // CRM conversational metrics (vertical-agnostic) — GET /admin/metrics/crm
    getCrm: (tenantId: string, from: string, to: string) => {
      const qs = new URLSearchParams({ tenantId, from, to });
      return request<import('@chatbot/shared-types').CrmMetricsResponse>(
        `/admin/metrics/crm?${qs.toString()}`,
      );
    },
    /**
     * Métricas de operaciones — GET /admin/metrics/cases (ADR-023).
     *
     * `caseType` decide contra qué set de estados se resuelve todo: los estados
     * son POR operación, y un tenant puede tener un pipeline de ventas distinto
     * al de sus tickets.
     */
    getCases: (tenantId: string, from: string, to: string, caseType = 'deal') => {
      const qs = new URLSearchParams({ tenantId, from, to, caseType });
      return request<import('@chatbot/shared-types').CaseMetricsResponse>(
        `/admin/metrics/cases?${qs.toString()}`,
      );
    },
    /**
     * Métricas de soporte — `GET /admin/metrics/tickets/:tenantId`.
     *
     * El tenant va en el PATH: el middleware compara contra el path param y no
     * mira el query string, así que el aislamiento lo garantiza él.
     */
    getTickets: (tenantId: string, from: string, to: string) => {
      const qs = new URLSearchParams({ from, to });
      return request<import('@chatbot/shared-types').TicketMetricsResponse>(
        `/admin/metrics/tickets/${tenantId}?${qs.toString()}`,
      );
    },
  },
};
